<template>
  <div>
    <!--begin::content-header-->
      <!--empty-->
    <!--end::content-header-->

    <!--begin::signin-->
    <div class="login-form login-signin">
      <div class="text-center mb-5">
        <h3 class="fs-1">
          {{ trans('auth.login.login') }}
        </h3>
        <p class="text-muted font-weight-semi-bold">
          {{ trans('auth.login.emailAndPassword') }}
        </p>
      </div>

      <!--begin::Login form-->
      <form @submit.stop.prevent="onSubmit">
        <!--begin::Input email-->
        <div class="mb-8 pt-10">
          <label for="login-email">{{ capitalize(transChoice('global.email', 0)) }}</label>
          <input
            id="login-email"
            type="email"
            class="form-control form-control-solid h-auto py-5 px-6"
            :class="{ 'is-invalid': validateState('email') === false || formErrors.message !== undefined }"
            name="login-email"
            v-model="v$.email.$model"
            :state="validateState('email')"
            :placeholder="trans('auth.login.yourEmail')"
            aria-describedby="login-email-live-feedback"
           />
           <div
            v-if="validateState('email') === false"
            id="login-email-live-feedback"
            class="text-danger"
          >
            <span v-if="formErrors.message === undefined">
              {{ trans('auth.login.messages.validEmailRequired') }}
            </span>
           </div>
        </div>
        <!--end::Input email-->

        <!--begin::Input password-->
        <div class="mb-3">
          <label for="input-password">{{ capitalize(transChoice('global.password', 1)) }}</label>
          <input
            id="input-password"
            type="password"
            class="form-control form-control-solid h-auto py-5 px-6"
            :class="{ 'is-invalid': validateState('password') === false || formErrors.message !== undefined }"
            name="input-password"
            v-model="v$.password.$model"
            :state="validateState('password')"
            :placeholder="trans('auth.login.yourPassword')"
            aria-describedby="input-password-live-feedback"
           />
           <div
            v-if="validateState('password') === false"
            id="input-password-live-feedback"
            class="text-danger"
          >
            <span v-if="formErrors.message === undefined">
              {{ trans('auth.login.messages.passwordRequired') }}
            </span>
           </div>
        </div>
        <!--end::Input password-->

        <!--begin::FirebaseAlert-->
        <div
          role="alert"
          :class="{ show: formErrors.message !== undefined }"
          class="alert fade alert-danger mt-8"
        >
          <div class="alert-text">
            {{ formErrorMessages[formErrors.message] }}
          </div>
        </div>
        <!--end::FirebaseAlert-->

        <!--begin::Action-->
        <div class="form-group d-flex flex-wrap justify-content-between align-items-center">
          <a
            id="nyg_login_forgot"
            href="#"
            class="text-dark-60 text-hover-primary my-3 me-2"
          >
            {{ trans('auth.login.forgotPassword') }}
          </a>

          <button
            ref="nyg_login_submit_btn"
            class="btn btn-primary font-weight-bold px-9 py-4 my-3 font-size-3"
            :disabled="submitButtonAnimation"
          >
            {{ submitButtonAnimation ? trans('auth.login.loggingIn') : trans('auth.login.login') }}
            <span
              class="spinner-border spinner-border-sm align-middle ms-2"
              :class="submitButtonAnimation ? 'd-inline-block' : 'd-none'"
            ></span>
          </button>
        </div>
        <!--end::Action-->
      </form>
      <!--end::Login form-->
    </div>
    <!--end::signin-->
  </div>
</template>

<script lang="ts">
  import { computed, ref, onBeforeMount } from 'vue'
  import { useStore } from 'vuex'
  import { useRouter } from 'vue-router'
  import { trans, transChoice } from '@src/core/helpers/translate'
  import { capitalize } from '@src/core/helpers/textUtils'
  import useFormValidation from '@src/use/formValidation'

  import useVuelidate from '@vuelidate/core'
  import { required, email, minLength } from '@vuelidate/validators'

  import { Actions, Mutations } from '@src/store/enums/StoreEnums'

  import fb from '@src/core/config/firebase'

  export default {
    name: 'LoginLayout',
    setup () {
      const store = useStore()
      const router = useRouter()

      onBeforeMount(() => {
        // store.commit(SET_ERROR, {}) // Remove all previous error-messages?
      })

      const submitted = ref(false)
      const submitButtonAnimation = ref(false)

      const formErrors = computed(() => store.state.AuthModule.errors) // computed vuex replaces mapstate
      const formEmail = ref('')
      const formPassword = ref('')

      const formErrorMessages = computed(() => ({
        'auth/user-disabled': trans('auth.login.messages.userDisabled'),
        'auth/user-not-found': trans('auth.login.messages.wrongUsernameOrPassword'),
        'auth/wrong-password': trans('auth.login.messages.wrongUsernameOrPassword'),
        'auth/too-many-requests': trans('auth.login.messages.tooManyRequests'),
        'auth/automatically-logged-out': trans('auth.login.messages.autologout'),
        'auth/operation-not-allowed': trans('auth.login.messages.providerDisabled'),
        'auth/configuration-not-found': trans('auth.login.messages.noConfigFound'),
        'auth/network-request-failed': trans('auth.login.messages.networkError'),
        'auth/missing-user-in-db': trans('auth.login.messages.missingUserInDB'), // Custom auth
        'auth/missing-token': trans('auth.login.messages.noToken'), // Custom auth
        'custom/api-unreachable': trans('auth.login.messages.apiUnreachable'), // Custom
        'misc/unknown-error': trans('auth.login.messages.unknownError')
      }))

      const firebaseErrors = () => formErrors.value.message === undefined // Custom validation rule

      const validationRules = {
        email: {
          required,
          email,
          firebaseErrors
        },
        password: {
          required,
          minLength: minLength(8),
          firebaseErrors
        }
      }

      const v$: any = useVuelidate(
        validationRules,
        {
          email: formEmail,
          password: formPassword
        }
      )

      const { validateState } = useFormValidation(v$, submitted)

      const onSubmit: any = async () => {
        store.commit(Mutations.SET_ERROR, {}) // Remove all previous error-messages
        submitted.value = true
        submitButtonAnimation.value = true

        v$.value.$touch()
        if (Object.keys(v$.value.$errors).length > 0) {
          submitButtonAnimation.value = false
          return // Found errors, will be shown in form - quit
        }

        await store.dispatch(Actions.LOGOUT)

        // Login with Firebase Auth Service
        const firebaseLoginResult = await fb.auth.signInWithEmailAndPassword(
          fb.auth.getAuth(),
          v$.value.email.$model,
          v$.value.password.$model
        )
          .then((user: any) => user)
          .catch((err: any) => {
            console.log('[FirebaseLoginResult ERROR:', err)
            store.commit(Mutations.SET_ERROR, { message: err.code })
            return err
          })

        // Fetch additional required userdata from Firestore DB (users collection)
        const validUserData = firebaseLoginResult.code === undefined
          ? await store.dispatch(Actions.LOGIN, { user: firebaseLoginResult.user, mode: 'manually' }) // eslint-disable-line object-property-newline, max-len
            .then((res: any) => res).catch((err: any) => {
              console.log('ERROR from VUEX LOGIN:', err)
              return err
            })
          : firebaseLoginResult // Hand off the ".code" to the IF statement

        if (validUserData.code !== undefined) {
          await store.dispatch(Actions.LOGOUT)
          store.commit(Mutations.SET_ERROR, { message: validUserData.code })
          submitButtonAnimation.value = false
          return
        }

        router.push('/dashboard').catch((err: any) => console.log(err))
        // root.$router.push('/dashboard').catch((err: any) => console.log(err))
      }

      return {
        formEmail,
        formPassword,
        formErrors,
        formErrorMessages,
        validateState,
        onSubmit,
        v$,
        trans,
        transChoice,
        capitalize,
        submitButtonAnimation
      }
    }
  }
</script>

<style lang="scss" scoped>
.spinner.spinner-right {
  padding-right: 3.5rem !important;
}
</style>
